import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types/api';
import { TOKEN_STORAGE_KEY } from '@/lib/constants';

/**
 * Admin boshqa foydalanuvchi nomidan ishlayotgan sessiya.
 *
 * Adminning O'Z tokenlari shu yerda saqlanadi va "Adminga qaytish" bosilganda
 * tiklanadi. Impersonation tokeni qasddan qisqa umrli va refresh token'siz
 * (backend/app/api/v1/users.py) — muddati tugasa ilova avtomatik admin
 * sessiyasiga qaytadi.
 */
export interface ImpersonationState {
  adminAccessToken: string;
  adminRefreshToken: string;
  adminUser: User;
  targetUser: User;
  impersonatorName: string;
  startedAt: number;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  impersonation: ImpersonationState | null;
  setSession: (params: { accessToken: string; refreshToken: string; user: User }) => void;
  updateUser: (user: User) => void;
  logout: () => void;
  /** Admin sessiyasini saqlab, maqsadli foydalanuvchi nomidan ishlashni boshlaydi */
  startImpersonation: (params: {
    accessToken: string;
    targetUser: User;
    impersonatorName: string;
  }) => void;
  /** Saqlangan admin sessiyasini tiklaydi. Qaytgan qiymat — kimdan chiqilgani */
  stopImpersonation: () => ImpersonationState | null;
}

/** Zustand auth store — token localStorage'da saqlanadi (persist middleware). */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      impersonation: null,
      setSession: ({ accessToken, refreshToken, user }) =>
        set({ accessToken, refreshToken, user, isAuthenticated: true }),
      updateUser: (user) => set({ user }),
      logout: () => {
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          impersonation: null,
        });
        // Keyingi kirgan xodim oldingisining keshini ko'rmasligi uchun (bitta
        // telefonda navbat bilan ishlash). Dinamik import — `identityCache` →
        // `queryClient` → `client` → `authStore` aylanasini oldini oladi.
        void import('@/lib/session/identityCache').then((m) => m.resetIdentityCaches());
      },

      startImpersonation: ({ accessToken, targetUser, impersonatorName }) => {
        const s = get();
        if (!s.accessToken || !s.refreshToken || !s.user) return;
        // Allaqachon impersonation ichida bo'lsa, ASL admin sessiyasi saqlanadi
        // (zanjir hosil bo'lmasligi uchun) — aks holda "qaytish" bir necha
        // bosqichga cho'zilib ketardi.
        const adminSession = s.impersonation ?? {
          adminAccessToken: s.accessToken,
          adminRefreshToken: s.refreshToken,
          adminUser: s.user,
        };
        set({
          accessToken,
          refreshToken: null,
          user: targetUser,
          isAuthenticated: true,
          impersonation: {
            adminAccessToken: adminSession.adminAccessToken,
            adminRefreshToken: adminSession.adminRefreshToken,
            adminUser: adminSession.adminUser,
            targetUser,
            impersonatorName,
            startedAt: Date.now(),
          },
        });
      },

      stopImpersonation: () => {
        const imp = get().impersonation;
        if (!imp) return null;
        set({
          accessToken: imp.adminAccessToken,
          refreshToken: imp.adminRefreshToken,
          user: imp.adminUser,
          isAuthenticated: true,
          impersonation: null,
        });
        return imp;
      },
    }),
    { name: TOKEN_STORAGE_KEY },
  ),
);
